import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, ArrowRight, Eye, EyeOff, Building2, Mail,
  Sparkles, Check, Zap, Rocket, RefreshCw, Gift
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Signup() {
  const { signup, verifyEmail, resendVerification } = useAuth();
  const nav = useNavigate();

  // Multi-step: 1 = Form, 2 = Verify Email (OTP/Link), 3 = Trial Active & Price Plans
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("trial");

  // Verification state
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";

  // Pricing cycle
  const [cycle, setCycle] = useState("monthly");

  // Timer countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedPlan) {
      toast.error("Please choose a trial or subscription plan. Accounts cannot be created without a selected plan.");
      return;
    }
    if (!form.name || !form.email || !form.password) {
      toast.error("Name, email and password are required");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      localStorage.clear();

      const res = await signup(form.name.trim(), form.email.trim(), form.password, { selected_plan: selectedPlan }, refCode);

      if (res?.requires_verification) {
        setStep(2);
        setResendCooldown(45);
        toast.success(`Verification code sent to ${form.email.trim()}`);
      } else {
        setStep(3);
        toast.success(`Account created! 7-day free trial active.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    const cleanOtp = otp.trim();
    if (!cleanOtp || cleanOtp.length < 6) {
      toast.error("Please enter the 6-digit confirmation code");
      return;
    }
    setVerifying(true);
    try {
      await verifyEmail(form.email.trim(), { otp: cleanOtp });
      localStorage.setItem("launchdeck_is_new_user", "true");
      setStep(3);
      toast.success("Email verified! Your 7-Day Free Trial is now active.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired verification code");
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResending(true);
    try {
      await resendVerification(form.email.trim());
      setResendCooldown(45);
      toast.success(`A fresh verification code has been sent to ${form.email.trim()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend code");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F8F9FA] dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Left panel - Branding (Shown on larger screens for step 1 & 2) */}
      {step !== 3 && (
        <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#0A0F29] via-[#0D1640] to-[#1E293B] text-white items-center justify-center p-12 relative overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/10 blur-3xl rounded-full" />
          <div className="relative z-10 max-w-md">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-10 rounded-xl bg-black border border-yellow-500/40 flex items-center justify-center font-black text-amber-400 text-sm shadow-xs">
                iHUB
              </div>
              <span className="font-extrabold text-2xl tracking-wide text-white font-heading">LaunchDeck</span>
            </div>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-500/20 text-blue-300 border border-blue-400/30 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> 7-Day Free Trial on Sign Up
            </div>

            <h1 className="font-bold text-4xl mb-4 leading-tight tracking-tight">Enterprise Multi-Location Marketing OS.</h1>
            <p className="text-slate-300 text-base leading-relaxed mb-6">
              Manage multiple studio locations, isolated client portals, automated reputation pipelines, and team members under one master dashboard.
            </p>

            <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2 text-xs text-slate-300">
              <div className="font-semibold text-white flex items-center gap-2">
                <Gift className="w-4 h-4 text-blue-400" /> Free 7-Day Trial Benefits:
              </div>
              <ul className="space-y-1.5 pl-6 list-disc">
                <li>Instant Pro access for 7 days</li>
                <li>Email confirmation via 6-digit OTP or 1-click magic link</li>
                <li>Zero credit card required upfront</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Main Container */}
      <div className={`flex-1 flex items-center justify-center p-6 sm:p-10 ${step === 3 ? "w-full max-w-5xl mx-auto" : ""}`}>
        {/* ── STEP 1: Signup Form ── */}
        {step === 1 && (
          <Card className="w-full max-w-lg p-8 border-slate-200 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-xl" data-testid="signup-card">
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold tracking-tight">Create your account</h2>
                <Badge className="bg-blue-600 text-white text-[11px] font-semibold">7 Days Free</Badge>
              </div>
              <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                Start your 7-day free trial with full platform access. No credit card required.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Full Name</Label>
                <Input
                  data-testid="signup-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Master"
                  className="mt-1 text-xs h-9"
                  required
                />
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Work Email</Label>
                <Input
                  data-testid="signup-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="master@launchdeck.com"
                  className="mt-1 text-xs h-9"
                  required
                />
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Password</Label>
                <div className="relative mt-1">
                  <Input
                    data-testid="signup-password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="At least 6 characters"
                    className="pr-10 text-xs h-9"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Mandatory Plan / 7-Day Trial Selection */}
              <div className="space-y-2 pt-1">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    Select Access Plan <span className="text-red-500">*</span>
                  </Label>
                  <span className="text-[10px] text-blue-600 dark:text-blue-400 font-semibold">Required to create account</span>
                </div>

                <div className="grid grid-cols-1 gap-2.5">
                  {/* Option 1: 7-Day Free Trial */}
                  <div
                    data-testid="plan-option-trial"
                    onClick={() => setSelectedPlan("trial")}
                    className={`p-3 rounded-xl border-2 transition-all cursor-pointer flex items-center justify-between ${
                      selectedPlan === "trial"
                        ? "border-blue-600 bg-blue-50/70 dark:bg-blue-950/40 shadow-xs"
                        : "border-slate-200 dark:border-slate-800 hover:border-slate-300 bg-white dark:bg-slate-900"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selectedPlan === "trial" ? "border-blue-600 bg-blue-600 text-white" : "border-slate-400"}`}>
                        {selectedPlan === "trial" && <Check className="w-3 h-3 stroke-[3]" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-900 dark:text-white">7-Day Free Trial</span>
                          <Badge className="bg-emerald-500 text-white text-[9px] px-1.5 py-0 h-4">Most Popular</Badge>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          Full Pro features for 7 days · $0 due today · Card not required
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-sm font-black text-blue-600 dark:text-blue-400">$0</span>
                    </div>
                  </div>

                  {/* Option 2: Pro Tier Subscription */}
                  <div
                    data-testid="plan-option-pro"
                    onClick={() => setSelectedPlan("pro")}
                    className={`p-3 rounded-xl border-2 transition-all cursor-pointer flex items-center justify-between ${
                      selectedPlan === "pro"
                        ? "border-blue-600 bg-blue-50/70 dark:bg-blue-950/40 shadow-xs"
                        : "border-slate-200 dark:border-slate-800 hover:border-slate-300 bg-white dark:bg-slate-900"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selectedPlan === "pro" ? "border-blue-600 bg-blue-600 text-white" : "border-slate-400"}`}>
                        {selectedPlan === "pro" && <Check className="w-3 h-3 stroke-[3]" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-900 dark:text-white">Pro Plan Subscription</span>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          Instant continuous access · Unlimited contacts &amp; campaigns
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-sm font-black text-slate-900 dark:text-white">$99</span>
                      <span className="text-[10px] text-slate-500">/mo</span>
                    </div>
                  </div>

                  {/* Option 3: Scale / Agency Plan */}
                  <div
                    data-testid="plan-option-scale"
                    onClick={() => setSelectedPlan("scale")}
                    className={`p-3 rounded-xl border-2 transition-all cursor-pointer flex items-center justify-between ${
                      selectedPlan === "scale"
                        ? "border-blue-600 bg-blue-50/70 dark:bg-blue-950/40 shadow-xs"
                        : "border-slate-200 dark:border-slate-800 hover:border-slate-300 bg-white dark:bg-slate-900"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selectedPlan === "scale" ? "border-blue-600 bg-blue-600 text-white" : "border-slate-400"}`}>
                        {selectedPlan === "scale" && <Check className="w-3 h-3 stroke-[3]" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-900 dark:text-white">Scale / Agency Tier</span>
                          <Badge className="bg-purple-600 text-white text-[9px] px-1.5 py-0 h-4">Agency</Badge>
                        </div>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          Multi-location sub-accounts · White labeling &amp; dedicated IP
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-sm font-black text-slate-900 dark:text-white">$199</span>
                      <span className="text-[10px] text-slate-500">/mo</span>
                    </div>
                  </div>
                </div>

                {!selectedPlan && (
                  <p className="text-[11px] text-red-500 font-medium">
                    ⚠️ You must choose a 7-day free trial or a subscription plan to create an account.
                  </p>
                )}
              </div>

              <Button
                data-testid="signup-submit"
                type="submit"
                disabled={loading || !selectedPlan}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-10 gap-2 shadow-xs mt-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                <span>
                  {selectedPlan === "trial"
                    ? "Start 7-Day Free Trial & Send Code"
                    : `Subscribe to ${selectedPlan === "scale" ? "Scale" : "Pro"} & Send Code`}
                </span>
              </Button>
            </form>

            <div className="mt-5 text-center text-xs text-slate-500">
              Already have an account?{" "}
              <Link to="/signin" className="text-blue-600 font-semibold hover:underline" data-testid="goto-signin">
                Sign in
              </Link>
            </div>
          </Card>
        )}

        {/* ── STEP 2: Email Confirmation (OTP or Link) ── */}
        {step === 2 && (
          <Card className="w-full max-w-lg p-8 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 rounded-2xl" data-testid="signup-verification-step">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-800 flex items-center justify-center mx-auto mb-3 text-blue-600">
                <Mail className="w-7 h-7" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight">Confirm Your Email</h2>
              <p className="text-slate-500 dark:text-slate-400 text-xs mt-2 max-w-sm mx-auto leading-relaxed">
                We've sent a 6-digit confirmation code and a 1-click confirmation link to:
              </p>
              <div className="mt-1.5 inline-block font-semibold text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-900">
                {form.email}
              </div>
            </div>

            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    Enter 6-Digit Code
                  </Label>
                  <span className="text-[11px] text-slate-500">Valid for 24h</span>
                </div>
                <Input
                  data-testid="signup-otp-input"
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="e.g. 123456"
                  className="h-12 text-center font-mono text-xl tracking-[0.4em] font-bold border-2 border-blue-500/30 focus-visible:border-blue-600"
                  autoFocus
                />
              </div>

              <div className="rounded-xl p-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-300 space-y-1">
                <div className="font-semibold flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
                  <Gift className="w-3.5 h-3.5" />
                  <span>Dual Confirmation Options</span>
                </div>
                <p className="leading-relaxed">
                  Type the 6-digit code above, or simply click the <strong>Verify Email &amp; Start 7-Day Free Trial</strong> link in your email!
                </p>
              </div>

              <Button
                data-testid="signup-verify-btn"
                type="submit"
                disabled={verifying || otp.length < 6}
                className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs shadow-md"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...
                  </>
                ) : (
                  <>
                    Verify &amp; Activate 7-Day Free Trial <ArrowRight className="w-4 h-4 ml-1.5" />
                  </>
                )}
              </Button>

              <div className="flex items-center justify-between pt-1 text-xs">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending || resendCooldown > 0}
                  className="text-blue-600 hover:underline flex items-center gap-1 font-medium disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${resending ? "animate-spin" : ""}`} />
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend code"}
                </button>

                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Change email
                </button>
              </div>
            </form>
          </Card>
        )}

        {/* ── STEP 3: 7-Day Free Trial Activated & Price Plans Showcase ── */}
        {step === 3 && (
          <div className="w-full py-6 space-y-8 animate-in fade-in duration-300" data-testid="trial-plans-step">
            {/* Celebration Card */}
            <div className="rounded-2xl p-6 sm:p-8 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-xl text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 blur-3xl rounded-full pointer-events-none" />
              <div className="relative z-10 space-y-3">
                <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-white/20 text-white backdrop-blur border border-white/25">
                  <Sparkles className="w-4 h-4" /> 7-Day Free Trial Activated!
                </div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                  Welcome to Your New Studio, {form.name || "Creator"}!
                </h1>
                <p className="text-sm sm:text-base text-blue-100 max-w-2xl mx-auto leading-relaxed">
                  Your email has been confirmed. You now have full unrestricted access to all <strong>Pro Tier Features</strong> for the next 7 days.
                </p>
                <div className="pt-2">
                  <Button
                    onClick={() => nav("/")}
                    data-testid="enter-dashboard-btn"
                    size="lg"
                    className="bg-white hover:bg-slate-100 text-blue-700 font-bold px-8 shadow-lg"
                  >
                    Go to Studio Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Price Plans Section */}
            <div className="space-y-6">
              <div className="text-center space-y-1.5">
                <h2 className="text-2xl font-bold tracking-tight">Our Price Plans</h2>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  Choose or upgrade your plan anytime. Zero commitments during your free trial.
                </p>

                {/* Billing cycle toggle */}
                <div className="inline-flex rounded-full p-1 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 mt-2">
                  <button
                    onClick={() => setCycle("monthly")}
                    className={`px-4 py-1 text-xs font-semibold rounded-full transition-all ${
                      cycle === "monthly" ? "bg-blue-600 text-white" : "text-slate-600 dark:text-slate-400"
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setCycle("yearly")}
                    className={`px-4 py-1 text-xs font-semibold rounded-full transition-all ${
                      cycle === "yearly" ? "bg-blue-600 text-white" : "text-slate-600 dark:text-slate-400"
                    }`}
                  >
                    Yearly <span className="opacity-80">· save 20%</span>
                  </button>
                </div>
              </div>

              {/* Grid of Plans */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* 1. Free Plan */}
                <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-400">
                        <Sparkles className="w-5 h-5" />
                      </div>
                      <div className="text-xl font-bold">Free</div>
                    </div>
                    <div className="mb-4">
                      <span className="text-4xl font-extrabold">$0</span>
                      <span className="text-xs text-slate-500 ml-1">forever</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">Basic essentials for individuals.</p>
                    <ul className="space-y-2.5 text-xs text-slate-600 dark:text-slate-300">
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 500 contacts</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 1,000 emails / month</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 20 AI generations / month</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 1 studio location</li>
                    </ul>
                  </div>
                  <Button variant="outline" className="w-full mt-6 text-xs" onClick={() => nav("/")}>
                    Basic Free Tier
                  </Button>
                </Card>

                {/* 2. Pro Plan (Active 7-Day Free Trial Tier) */}
                <Card className="p-6 border-2 border-blue-600 dark:border-blue-500 bg-white dark:bg-slate-900 rounded-xl shadow-xl relative -translate-y-2 flex flex-col justify-between">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold text-white bg-blue-600 shadow-sm flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> YOUR ACTIVE 7-DAY TRIAL
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-3 mt-2">
                      <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center text-blue-600">
                        <Zap className="w-5 h-5" />
                      </div>
                      <div className="text-xl font-bold">Pro Tier</div>
                    </div>
                    <div className="mb-2">
                      <span className="text-4xl font-extrabold">${cycle === "yearly" ? 23 : 29}</span>
                      <span className="text-xs text-slate-500 ml-1">/mo after trial</span>
                    </div>
                    <Badge className="bg-emerald-500 text-white text-[11px] mb-4">
                      Active · 7 Days Remaining
                    </Badge>
                    <p className="text-xs text-slate-500 mb-4">Unrestricted access to all professional marketing tools.</p>
                    <ul className="space-y-2.5 text-xs text-slate-600 dark:text-slate-300">
                      <li className="flex items-center gap-2 font-medium text-blue-600 dark:text-blue-400">
                        <Check className="w-4 h-4 text-blue-600" /> 5,000 contacts &amp; CRM
                      </li>
                      <li className="flex items-center gap-2 font-medium text-blue-600 dark:text-blue-400">
                        <Check className="w-4 h-4 text-blue-600" /> 25,000 email sends / month
                      </li>
                      <li className="flex items-center gap-2 font-medium text-blue-600 dark:text-blue-400">
                        <Check className="w-4 h-4 text-blue-600" /> 500 AI generations &amp; Copilot
                      </li>
                      <li className="flex items-center gap-2 font-medium text-blue-600 dark:text-blue-400">
                        <Check className="w-4 h-4 text-blue-600" /> 5 team members &amp; sub-accounts
                      </li>
                      <li className="flex items-center gap-2 font-medium text-blue-600 dark:text-blue-400">
                        <Check className="w-4 h-4 text-blue-600" /> Automated pipelines &amp; campaigns
                      </li>
                    </ul>
                  </div>

                  <Button
                    onClick={() => nav("/")}
                    className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-10"
                  >
                    Enter Dashboard on Free Trial &rarr;
                  </Button>
                </Card>

                {/* 3. Scale / Agency Plan */}
                <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center text-orange-600">
                        <Rocket className="w-5 h-5" />
                      </div>
                      <div className="text-xl font-bold">Scale / Agency</div>
                    </div>
                    <div className="mb-4">
                      <span className="text-4xl font-extrabold">${cycle === "yearly" ? 79 : 99}</span>
                      <span className="text-xs text-slate-500 ml-1">/mo</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">For agencies managing high client volume & multiple studios.</p>
                    <ul className="space-y-2.5 text-xs text-slate-600 dark:text-slate-300">
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> Unlimited contacts</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> 100,000 emails / month</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> 5,000 AI generations</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> Unlimited locations &amp; sub-accounts</li>
                    </ul>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full mt-6 text-xs border-orange-500/40 text-orange-600 hover:bg-orange-500/10"
                    onClick={() => nav("/billing")}
                  >
                    Upgrade to Agency Scale
                  </Button>
                </Card>
              </div>

              {/* Bottom direct link */}
              <div className="text-center pt-2">
                <Button
                  onClick={() => nav("/")}
                  variant="ghost"
                  className="text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                >
                  Continue directly to Dashboard &rarr;
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
